import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useState, useEffect, useCallback } from 'react';
import { Model } from '../../models';
import useApiCallable from '../../api/use-api-callable';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import { Api } from '../../api';

interface EditDiagnosisModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  existingDiagnoses: Api.UserStats.V['user']['diagnoses'];
  existingGeneralHealthConditions?: Api.UserStats.V['user']['generalHealthConditions'];
}

export default function EditDiagnosisModal({
  open,
  onClose,
  userId,
  existingDiagnoses,
  existingGeneralHealthConditions = [],
}: EditDiagnosisModalProps) {
  const callApi = useApiCallable();
  const { invalidateExact } = useInvalidation();

  const [selectedDiagnoses, setSelectedDiagnoses] = useState<string[]>([]);
  const [selectedGeneralHealth, setSelectedGeneralHealth] = useState<string[]>(
    [],
  );
  const [diagnosisDetails, setDiagnosisDetails] = useState<
    Record<
      string,
      { diagnosedBy: string; diagnosedAge: number; managements: string[] }
    >
  >({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});

  const questions = Model.Questionnaire.getDiagnosisQuestions();
  const options = Model.Questionnaire.getDiagnosisOptions();

  const diagnosisOptions = options.womensHealthOptions.filter(
    (opt) => !['no_diagnosis', 'healthy'].includes(opt.value),
  );

  useEffect(() => {
    if (open) {
      const diagnosisTypes = existingDiagnoses.map((d) => d.type);
      setSelectedDiagnoses(diagnosisTypes);

      const details: Record<string, any> = {};
      existingDiagnoses.forEach((d) => {
        details[d.type] = {
          diagnosedBy: d.diagnosedBy,
          diagnosedAge: d.diagnosedAge,
          managements: d.managements,
        };
      });
      setDiagnosisDetails(details);

      setSelectedGeneralHealth(
        existingGeneralHealthConditions as unknown as string[],
      );
    }
  }, [open, existingDiagnoses, existingGeneralHealthConditions]);

  const handleDiagnosisChange = useCallback(
    (diagnosisValue: string, checked: boolean) => {
      if (checked) {
        setSelectedDiagnoses((prev) => [...prev, diagnosisValue]);
        setDiagnosisDetails((prev) => {
          if (prev[diagnosisValue]) {
            return prev;
          }
          return {
            ...prev,
            [diagnosisValue]: {
              diagnosedBy: '',
              diagnosedAge: 0,
              managements: [],
            },
          };
        });
      } else {
        setSelectedDiagnoses((prev) =>
          prev.filter((d) => d !== diagnosisValue),
        );
      }
    },
    [],
  );

  const handleGeneralHealthChange = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        setSelectedGeneralHealth((prev) => [...prev, value]);
      } else {
        setSelectedGeneralHealth((prev) => prev.filter((v) => v !== value));
      }
    },
    [],
  );

  const handleManagementChange = useCallback(
    (diagnosisType: string, management: string, checked: boolean) => {
      setDiagnosisDetails((prev) => ({
        ...prev,
        [diagnosisType]: {
          ...prev[diagnosisType],
          managements: checked
            ? [...prev[diagnosisType].managements, management]
            : prev[diagnosisType].managements.filter((m) => m !== management),
        },
      }));
    },
    [],
  );

  const updateDiagnosisField = useCallback(
    (diagnosisType: string, field: string, value: any) => {
      setDiagnosisDetails((prev) => ({
        ...prev,
        [diagnosisType]: {
          ...prev[diagnosisType],
          [field]: value,
        },
      }));
    },
    [],
  );

  const handleSave = async () => {
    const diagnoses = selectedDiagnoses
      .map((type) => ({
        type,
        diagnosedBy: diagnosisDetails[type]?.diagnosedBy || '',
        diagnosedAge: diagnosisDetails[type]?.diagnosedAge || 0,
        managements: diagnosisDetails[type]?.managements || [],
      }))
      .filter((d) => d.diagnosedBy && d.diagnosedAge > 0);

    const processedGeneralHealth = selectedGeneralHealth.map((condition) => {
      if (condition === 'other' && otherTexts['q25']) {
        return otherTexts['q25'];
      }
      return condition;
    });

    await callApi(`/users/${userId}/as-admin`, {
      method: 'PATCH',
      jsonBody: {
        diagnoses,
        generalHealthConditions: processedGeneralHealth,
      },
    });

    invalidateExact(`/users/${userId}/stats`);
    toast.success('Diagnosis updated successfully');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Edit Diagnosis</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Users have the ability to overwrite your changes in the app
          </Typography>

          {/* Women's Health Diagnoses */}
          <Box
            sx={{
              bgcolor: 'var(--mui-palette-background-level1)',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {questions.womensHealth}
              </Typography>
              <Stack spacing={1}>
                {diagnosisOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={selectedDiagnoses.includes(option.value)}
                        onChange={(e) =>
                          handleDiagnosisChange(option.value, e.target.checked)
                        }
                      />
                    }
                    label={option.label}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>

          {/* Questions for each selected diagnosis */}
          {selectedDiagnoses.map((diagnosisType) => {
            const diagnosisLabel =
              diagnosisOptions.find((opt) => opt.value === diagnosisType)
                ?.label || diagnosisType;

            return (
              <>
                {/* Who diagnosed */}
                <Box
                  key={`${diagnosisType}-diagnosedBy`}
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">
                      {diagnosisLabel}: {questions.diagnosedBy}
                    </Typography>
                    <Stack spacing={1}>
                      {options.diagnosedByOptions.map((option) => (
                        <FormControlLabel
                          key={`${diagnosisType}-${option.value}`}
                          control={
                            <Checkbox
                              checked={
                                diagnosisDetails[diagnosisType]?.diagnosedBy ===
                                option.value
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  updateDiagnosisField(
                                    diagnosisType,
                                    'diagnosedBy',
                                    option.value,
                                  );
                                }
                              }}
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Box>

                {/* Age at diagnosis */}
                <Box
                  key={`${diagnosisType}-diagnosedAge`}
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">
                      {diagnosisLabel}: {questions.diagnosedAge}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      variant="outlined"
                      size="small"
                      value={
                        diagnosisDetails[diagnosisType]?.diagnosedAge || ''
                      }
                      onChange={(e) =>
                        updateDiagnosisField(
                          diagnosisType,
                          'diagnosedAge',
                          parseInt(e.target.value) || 0,
                        )
                      }
                      inputProps={{ min: 0, max: 100 }}
                      required
                    />
                  </Stack>
                </Box>

                {/* Management */}
                <Box
                  key={`${diagnosisType}-management`}
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">
                      {diagnosisLabel}: {questions.management}
                    </Typography>
                    <Stack spacing={1}>
                      {options.managementOptions.map((option) => (
                        <FormControlLabel
                          key={`${diagnosisType}-${option.value}`}
                          control={
                            <Checkbox
                              checked={
                                diagnosisDetails[
                                  diagnosisType
                                ]?.managements?.includes(option.value) || false
                              }
                              onChange={(e) =>
                                handleManagementChange(
                                  diagnosisType,
                                  option.value,
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </>
            );
          })}

          {/* General Health Conditions */}
          <Box
            sx={{
              bgcolor: 'var(--mui-palette-background-level1)',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {questions.generalHealthConditions}
              </Typography>
              <Stack spacing={1}>
                {options.generalHealthOptions.map((option) => (
                  <Box key={option.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedGeneralHealth.includes(option.value)}
                          onChange={(e) =>
                            handleGeneralHealthChange(
                              option.value,
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={option.label}
                    />
                    {option.additionalInfo &&
                      selectedGeneralHealth.includes(option.value) && (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Please specify..."
                          value={otherTexts['q25'] || ''}
                          onChange={(e) =>
                            setOtherTexts((prev) => ({
                              ...prev,
                              q25: e.target.value,
                            }))
                          }
                          sx={{ ml: 4, mt: 1 }}
                        />
                      )}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
